/**
 * A simple logger utility that adds module context and standardizes log formats
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private module: string;

  constructor(module: string) {
    this.module = module;
  }

  /**
   * Format a log message with timestamp and module name
   */
  private formatMessage(level: LogLevel, message: string): string {
    return `[${new Date().toISOString()}] [${level.toUpperCase()}] [${this.module}] ${message}`;
  }

  /**
   * Log a debug message
   */
  debug(message: string, ...args: unknown[]): void {
    console.debug(this.formatMessage('debug', message), ...args);
  }

  /**
   * Log an info message
   */
  info(message: string, ...args: unknown[]): void {
    console.info(this.formatMessage('info', message), ...args);
  }

  /**
   * Log a warning message
   */
  warn(message: string, ...args: unknown[]): void {
    console.warn(this.formatMessage('warn', message), ...args);
  }

  /**
   * Log an error message
   */
  error(message: string, ...args: unknown[]): void {
    console.error(this.formatMessage('error', message), ...args);
  }
}

/**
 * Create a logger for a specific module
 * @param module The module name
 * @returns A logger instance
 */
export const getLogger = (module: string): Logger => {
  return new Logger(module);
};

export default getLogger;